import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AbsencesService } from '@wilson/absences';
import { Absence } from '@wilson/interfaces';
import {
  GanttEndingPixelPipe,
  GanttStartingPixelPipe,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import {
  CreateNewViaDragAndDropService,
  DragAndDropModeService,
} from '@wilson/shift-timeline/services';
import {
  SetDemarcatorRange,
  ShiftTimelineState,
  TimelineRemoveAbsence,
  TimelineStaysSuggestionService,
  TimelineUpsertNewAbsence,
} from '@wilson/shift-timeline/state';
import { NzMessageService } from 'ng-zorro-antd/message';
import { firstValueFrom } from 'rxjs';
import { TimelineCommonOperationsService } from '../../services/timeline-common-operations.service';

@Injectable()
export class MoveAbsenceHelperService {
  private readonly dragAndDropModeService = inject(DragAndDropModeService);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly createNewViaDragAndDropService = inject(
    CreateNewViaDragAndDropService,
  );
  private readonly absencesService = inject(AbsencesService);
  private readonly messageService = inject(NzMessageService);
  private readonly ganttActivityStartingPixelPipe = inject(
    GanttStartingPixelPipe,
  );
  private readonly ganttActivityEndingPixelPipe = inject(GanttEndingPixelPipe);
  private readonly timelineCommonOperationsService = inject(
    TimelineCommonOperationsService,
  );
  private readonly timelineStaysSuggestionService = inject(
    TimelineStaysSuggestionService,
  );

  private readonly ADJUSTED_LEFT_PX = 210;
  private mockAbsence!: Absence & { id: string; isHighlighted: boolean };
  public absenceWidth = this.ADJUSTED_LEFT_PX;

  public async setAbsenceWidth(): Promise<void> {
    const draggingAbsence = await firstValueFrom(
      this.dragAndDropModeService.draggingAbsence$,
    );
    if (draggingAbsence) {
      const absenceLeftPx = await firstValueFrom(
        this.ganttActivityStartingPixelPipe.transform(
          draggingAbsence.absentFrom,
        ),
      );
      const absenceRightPx = await firstValueFrom(
        this.ganttActivityEndingPixelPipe.transform([
          draggingAbsence.absentFrom,
          draggingAbsence.absentTo,
        ]),
      );
      const frameLength = this.store.selectSnapshot(
        ShiftTimelineState.frameLength,
      );
      this.absenceWidth = frameLength - absenceRightPx - absenceLeftPx;
    }
  }

  public async onDragOverHandler(
    mockAbsence: Absence & {
      id: string;
      isHighlighted: boolean;
    },
  ): Promise<void> {
    const draggingAbsence = await firstValueFrom(
      this.dragAndDropModeService.draggingAbsence$,
    );
    if (draggingAbsence) {
      this.mockAbsence = mockAbsence;

      if (this.mockAbsence) {
        this.store.dispatch(
          new TimelineUpsertNewAbsence({
            absence: this.mockAbsence,
          }),
        );
        this.dragAndDropModeService.setDraggingItemId(draggingAbsence.id);
      }
    }
  }

  public async onDragLeaveHandler(): Promise<void> {
    const draggingAbsence = await firstValueFrom(
      this.dragAndDropModeService.draggingAbsence$,
    );
    if (draggingAbsence) {
      this.removeMockAbsenceFromState(
        draggingAbsence.id,
        draggingAbsence.userId,
      );
      this.store.dispatch(
        new TimelineUpsertNewAbsence({
          absence: draggingAbsence,
        }),
      );
    }
    this.dragAndDropModeService.setDraggingItemId(null);
  }

  public async onDropHandler(): Promise<void> {
    const draggingAbsence = await firstValueFrom(
      this.dragAndDropModeService.draggingAbsence$,
    );
    if (this.mockAbsence && draggingAbsence) {
      this.createNewViaDragAndDropService.addUpdatingId(draggingAbsence.id);
      this.dragAndDropModeService.setDraggingItemId(null);
      this.store.dispatch(new SetDemarcatorRange([]));
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isHighlighted, ...absenceToUpdate } = this.mockAbsence;
        await firstValueFrom(
          this.absencesService.updateAbsence(
            absenceToUpdate,
            absenceToUpdate.id,
          ),
        );
        this.timelineStaysSuggestionService.refreshStaysDataForSpecifiedUsers([
          absenceToUpdate.userId,
        ]);
        this.timelineCommonOperationsService.updateProductivityAndKpisOfUsers([
          absenceToUpdate.userId,
        ]);

        this.createNewViaDragAndDropService.removeUpdatingId(
          draggingAbsence.id,
        );
        this.dragAndDropModeService.setDraggingAbsence(null);
      } catch {
        this.removeMockAbsenceFromState(
          draggingAbsence.id,
          draggingAbsence.userId,
        );
        this.store.dispatch(
          new TimelineUpsertNewAbsence({
            absence: draggingAbsence,
          }),
        );
        this.createNewViaDragAndDropService.removeUpdatingId(
          draggingAbsence.id,
        );
        this.dragAndDropModeService.setDraggingAbsence(null);
        this.messageService.error(this.translate.instant('general.error'));
      }
      this.dragAndDropModeService.setTimeDifferenceVisible(true);
    }
  }

  private removeMockAbsenceFromState(absenceId: string, userId: string): void {
    this.store.dispatch(
      new TimelineRemoveAbsence({
        absenceId: absenceId,
        userId: userId,
      }),
    );
  }
}
